import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { careerAPI } from '../../../services/careerApi';
import { CareerPosting } from '../../../types/career';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import EmptyState from '../../../components/ui/EmptyState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { usePageTitle } from '../../../hooks/usePageTitle';

export default function CareerList() {
  usePageTitle('Careers');
  const navigate = useNavigate();
  const [postings, setPostings] = useState<CareerPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    careerAPI
      .fetchStaffPostings()
      .then((rows) => {
        setPostings(rows);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to load careers')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (posting: CareerPosting) => {
    if (!window.confirm(`Delete “${posting.title}”?`)) return;
    setDeletingId(posting.id);
    try {
      await careerAPI.deletePosting(posting.id);
      setPostings((current) => current.filter((item) => item.id !== posting.id));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete posting'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Careers' }]} />
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Careers</h1>
          <p className="text-gray-600 mt-2">Publish openings. Each Apply link goes to the hiring office or sponsor.</p>
        </div>
        <Button onClick={() => navigate('/admin/careers/new')}>
          <PlusIcon className="h-5 w-5 mr-2" />
          New posting
        </Button>
      </div>
      {error && <ErrorState error={error} title="Could not load careers" onRetry={load} />}
      {loading && <LoadingSkeleton lines={6} />}
      {!loading && !error && postings.length === 0 && (
        <EmptyState
          title="No postings yet"
          description="Create a posting when an office or sponsor is hiring."
          onAction={() => navigate('/admin/careers/new')}
          actionLabel="New posting"
        />
      )}
      {!loading && postings.length > 0 && (
        <div className="space-y-3">
          {postings.map((posting) => (
            <Card key={posting.id} className="!p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">{posting.title}</h2>
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{posting.status}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{posting.sponsor}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/admin/careers/${posting.id}/edit`}
                    className="inline-flex items-center justify-center h-11 w-11 text-primary hover:text-primary-dark"
                    aria-label={`Edit ${posting.title}`}
                  >
                    <PencilIcon className="h-5 w-5" />
                  </Link>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center h-11 w-11 text-gray-500 hover:text-error disabled:opacity-50"
                    aria-label={`Delete ${posting.title}`}
                    disabled={deletingId === posting.id}
                    onClick={() => remove(posting)}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
